import type { GetActiveProvidersResponse } from '#services/api/insights.ts';

import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { activeProviders } from '#lib/react-query/queries.ts';
import { Base } from '#routes/Home/Widgets/Base.tsx';
import {
  InfoCalculation,
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import {
  CHART_AXIS_STYLES,
  CHART_SERIES_COLORS,
  CHART_X_AXIS_CONFIG,
  WIDGET_CHART_HEIGHT,
} from '#utils/charts.ts';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useAppointmentWidget } from './use-appointment-widget.ts';

const widgetTitle = 'Active Providers';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Counts the number of&nbsp;
        <InfoEmphasis>unique providers</InfoEmphasis>
        &nbsp;who delivered care during the selected time period. This metric helps organizations
        measure clinical capacity, track staff utilization, and monitor operational coverage.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoCalculation
        formula="Active Providers ="
        steps={[
          'Count of unique providers',
          'From appointments that are not canceled or deleted',
          'Where the appointment was not a no-show',
          'Provider information is available (not blank)',
          'Respects all applied filters (date, location, etc.)',
        ]}
      />

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments with a valid Provider Id',
            'Appointments that are not canceled or deleted',
            'Appointments with a start time within the selected date range',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Canceled appointments',
            'Deleted appointments',
            'No-showed appointments',
            'Appointments without an assigned provider',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              A provider is counted&nbsp;
              <InfoEmphasis>once</InfoEmphasis>
              &nbsp;per date range, even if they delivered care in multiple appointments
            </>,
            'Based on attended appointments only; canceled and no-show visits are not counted toward active provider totals',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

/**
 * Post-processes the response from the form completion time trends
 * used to memoize to prevent executing the function on every render.
 */
function postProcessResponse(response: GetActiveProvidersResponse) {
  // Make any necessary data transformations on the response here
  return {
    ...response,
    data: response.data.map((item) => {
      return {
        ...item,
        period: new Date(item.period),
      };
    }),
  };
}

export function ActiveProviders() {
  const widget = useAppointmentWidget();

  const theme = useTheme();

  // Fetch form trends data
  const { data, isLoading } = useQuery({
    ...activeProviders(widget.getCommonRequestParams()),
    select: postProcessResponse,
  });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      id="widget-active-providers"
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.data.length > 0 ? (
            <ChartDataProvider
              colors={(mode) => CHART_SERIES_COLORS[mode]}
              dataset={data.data}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              // Need to set right marin to fit x-axis tick label
              margin={{ right: 30 }}
              series={[
                {
                  dataKey: 'activeProviders',
                  label: 'Active Providers',
                  labelMarkType: 'circle',
                  type: 'line',
                },
              ]}
              xAxis={[
                {
                  dataKey: 'period',
                  height: 60,
                  label: 'Dates',
                  scaleType: 'time',
                  ...CHART_X_AXIS_CONFIG[data.groupedBy],
                },
              ]}
              yAxis={[
                {
                  label: 'Active Providers',
                },
              ]}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              <ChartsSurface>
                <ChartsXAxis sx={CHART_AXIS_STYLES(theme)} />
                <ChartsYAxis sx={CHART_AXIS_STYLES(theme)} />
                <LinePlot />
                <MarkPlot />
                <ChartsAxisHighlight x="line" y="none" />
                <ChartsTooltip />
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
